/**
 * Время. В базе — UTC, на экране — местное время производства.
 *
 * Часовой пояс производства: Самара, UTC+4, перехода на летнее время нет,
 * поэтому фиксированное смещение точно и не требует библиотеки часовых поясов.
 *
 * ГЛАВНОЕ ПРАВИЛО: смена относится к ДАТЕ СВОЕГО НАЧАЛА.
 * Смена 22:00 3 марта — 07:00 4 марта считается сменой за 3 марта.
 */
export const PRODUCTION_TZ = 'Europe/Samara';
export const PRODUCTION_TZ_OFFSET_MINUTES = 240;
const MS_IN_MINUTE = 60_000;
const MINUTES_IN_DAY = 1440;
function pad(n, width = 2) {
    return String(n).padStart(width, '0');
}
/** Момент времени → местные дата и минута суток. */
export function toLocalParts(instant, offsetMinutes = PRODUCTION_TZ_OFFSET_MINUTES) {
    const shifted = new Date(instant.getTime() + offsetMinutes * MS_IN_MINUTE);
    const date = `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
    const minuteOfDay = shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
    return { date, minuteOfDay };
}
/** Местные дата и минута суток → момент времени в UTC. */
export function fromLocalParts(date, minuteOfDay, offsetMinutes = PRODUCTION_TZ_OFFSET_MINUTES) {
    const [y, m, d] = date.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined) {
        throw new Error(`fromLocalParts: неверная дата «${date}»`);
    }
    return new Date(Date.UTC(y, m - 1, d) + (minuteOfDay - offsetMinutes) * MS_IN_MINUTE);
}
export function addDays(date, days) {
    const [y, m, d] = date.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined) {
        throw new Error(`addDays: неверная дата «${date}»`);
    }
    const shifted = new Date(Date.UTC(y, m - 1, d + days));
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}
/** Начало смены в UTC. Дата — дата начала смены. */
export function shiftStartUtc(date, window, offsetMinutes = PRODUCTION_TZ_OFFSET_MINUTES) {
    return fromLocalParts(date, window.startMinute, offsetMinutes);
}
/** Конец смены в UTC. Для ночной смены попадает на следующие сутки — это нормально. */
export function shiftEndUtc(date, window, offsetMinutes = PRODUCTION_TZ_OFFSET_MINUTES) {
    return new Date(shiftStartUtc(date, window, offsetMinutes).getTime() + window.durationMinutes * MS_IN_MINUTE);
}
/**
 * Какой смене принадлежит момент времени.
 * Возвращает номер смены и дату НАЧАЛА этой смены (см. правило ночной смены).
 * null — если участок в это время не работает (например, смешивание ночью).
 */
export function resolveShift(instant, windows, offsetMinutes = PRODUCTION_TZ_OFFSET_MINUTES) {
    const { date, minuteOfDay } = toLocalParts(instant, offsetMinutes);
    for (const w of windows) {
        const start = w.startMinute;
        const end = start + w.durationMinutes;
        if (end <= MINUTES_IN_DAY) {
            if (minuteOfDay >= start && minuteOfDay < end) {
                return { date, shiftNumber: w.shiftNumber };
            }
        }
        else {
            // Смена переходит через полночь.
            if (minuteOfDay >= start) {
                return { date, shiftNumber: w.shiftNumber };
            }
            if (minuteOfDay < end - MINUTES_IN_DAY) {
                // Уже следующие календарные сутки, но смена началась вчера.
                return { date: addDays(date, -1), shiftNumber: w.shiftNumber };
            }
        }
    }
    return null;
}
/** Понедельник недели, в которую попадает дата. */
export function weekStart(date) {
    const [y, m, d] = date.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined) {
        throw new Error(`weekStart: неверная дата «${date}»`);
    }
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 — воскресенье
    const shift = dow === 0 ? -6 : 1 - dow;
    return addDays(date, shift);
}
/** «07:00», «14:30» — из минут суток. */
/** «ЧЧ:ММ» → минута суток. Для слотов собеседований, которые задаются текстом. */
export function parseMinuteOfDay(time) {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
    if (!match)
        throw new Error(`Время «${time}» не в формате ЧЧ:ММ`);
    return Number(match[1]) * 60 + Number(match[2]);
}
export function formatMinuteOfDay(minuteOfDay) {
    const normalized = ((minuteOfDay % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
    return `${pad(Math.floor(normalized / 60))}:${pad(normalized % 60)}`;
}
export function formatShiftWindow(window) {
    return `${formatMinuteOfDay(window.startMinute)}–${formatMinuteOfDay(window.startMinute + window.durationMinutes)}`;
}
/** «2026-03-03» → «03.03.2026». */
export function formatDateRu(date) {
    const [y, m, d] = date.split('-');
    if (y === undefined || m === undefined || d === undefined) {
        throw new Error(`formatDateRu: неверная дата «${date}»`);
    }
    return `${d}.${m}.${y}`;
}
/** Часы отдыха между концом одной смены и началом следующей. */
export function restHours(previousEndUtc, nextStartUtc) {
    return (nextStartUtc.getTime() - previousEndUtc.getTime()) / (60 * MS_IN_MINUTE);
}
/**
 * Дедлайн плановой заявки на отсутствие: не позже чем за 48 часов до начала смены.
 * Экстренный невыход этой проверке НЕ подчиняется.
 */
export const PLANNED_ABSENCE_DEADLINE_HOURS = 48;
export function isPlannedAbsenceAllowed(now, shiftStart, deadlineHours = PLANNED_ABSENCE_DEADLINE_HOURS) {
    return shiftStart.getTime() - now.getTime() >= deadlineHours * 60 * MS_IN_MINUTE;
}
//# sourceMappingURL=time.js.map